import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSIconButton from '../../../design-system/DSIconButton';
import { formatFrameOffset } from '../../../utils/klydoThumbnail';

export type FilmstripFrame = {
  offsetSeconds: number;
  url?: string;
};

type KlydoThumbnailFilmstripProps = {
  frames: FilmstripFrame[];
  selectedOffsetSeconds?: number;
  onSelect: (offsetSeconds: number) => void;
};

const KlydoThumbnailFilmstrip = ({
  frames,
  selectedOffsetSeconds,
  onSelect,
}: KlydoThumbnailFilmstripProps) => {
  const { spacing, palette } = useTheme();

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: spacing(1),
        justifyContent: 'space-between',
      }}
    >
      {frames.map((frame) => {
        const isSelected = frame.offsetSeconds === selectedOffsetSeconds;

        return (
          <DSIconButton
            key={frame.offsetSeconds}
            aria-label={`Use the frame at ${formatFrameOffset(frame.offsetSeconds)}`}
            aria-pressed={isSelected}
            onClick={() => onSelect(frame.offsetSeconds)}
            disableRipple
            sx={{
              width: spacing(9),
              height: spacing(9),
              padding: 0,
              flexShrink: 0,
              cursor: 'pointer',
              overflow: 'hidden',
              borderRadius: spacing(1.5),
              backgroundColor: palette.white[5],
              border: isSelected
                ? `2px solid ${palette.primary.main}`
                : '2px solid transparent',
              transition: 'border-color 0.2s ease, opacity 0.2s ease',
              opacity: isSelected ? 1 : 0.7,
              '&:hover': { opacity: 1 },
              '&:focus-visible': {
                outline: `2px solid ${palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            {frame.url && (
              <img
                src={frame.url}
                alt=""
                loading="lazy"
                draggable={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}
          </DSIconButton>
        );
      })}
    </DSBox>
  );
};

export default KlydoThumbnailFilmstrip;
